/**
 * GSA Tool Configurations
 *
 * Tools for GSA Schedule pricing and contractor lookup.
 */ import { defineToolConfig, formatCount, hasProperty } from '../tool-display-config';
// ============================================================================
// Tool Configurations
// ============================================================================
const gsaPricingConfig = defineToolConfig({
    id: 'gsa-pricing',
    aliases: [
        'gsaPricing',
        'gsaPricingTool'
    ],
    formatSuccess: (output)=>{
        if (!output.success) return 'Pricing search completed with issues';
        const count = output.laborCategories?.length ?? 0;
        if (count === 0) return 'No pricing data found';
        const stats = output.statistics;
        if (stats) {
            const avg = stats.avgRate.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0
            });
            return `Found ${formatCount(count, 'rate')} (avg: ${avg}/hr)`;
        }
        return `Found ${formatCount(count, 'rate')}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'error')) {
            return `GSA pricing search failed: ${error.error}`;
        }
        if (hasProperty(error, 'message')) {
            return `GSA pricing search failed: ${error.message}`;
        }
        return 'Failed to search GSA pricing';
    },
    formatInput: (input)=>{
        if (!input?.laborCategory) return [];
        const fields = [
            {
                label: 'Labor Category',
                value: input.laborCategory
            }
        ];
        if (input.experience) fields.push({
            label: 'Experience',
            value: input.experience
        });
        if (input.education) fields.push({
            label: 'Education',
            value: input.education
        });
        if (input.minRate || input.maxRate) {
            fields.push({
                label: 'Rate Range',
                value: `$${input.minRate ?? 0} - $${input.maxRate ?? '∞'}`
            });
        }
        return fields;
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output && 'laborCategories' in output;
    },
    isEmptyResult: (output)=>!output.laborCategories?.length,
    isErrorResult: (output)=>!output.success
});
const gsaContractorLookupConfig = defineToolConfig({
    id: 'gsa-contractor-lookup',
    aliases: [
        'gsaContractorLookup',
        'gsaContractorLookupTool'
    ],
    formatSuccess: (output)=>{
        if (!output.success) return 'Contractor lookup completed with issues';
        const count = output.contractors?.length ?? 0;
        if (count === 0) return 'No contractors found';
        return `Found ${formatCount(count, 'contractor')}`;
    },
    formatError: (error)=>{
        if (hasProperty(error, 'error')) {
            return `Contractor lookup failed: ${error.error}`;
        }
        if (hasProperty(error, 'message')) {
            return `Contractor lookup failed: ${error.message}`;
        }
        return 'Failed to lookup GSA contractors';
    },
    formatInput: (input)=>{
        if (!input) return [];
        const fields = [];
        if (input.name) fields.push({
            label: 'Name',
            value: input.name
        });
        if (input.sin) fields.push({
            label: 'SIN',
            value: input.sin
        });
        if (input.contractNumber) fields.push({
            label: 'Contract #',
            value: input.contractNumber
        });
        return fields.length > 0 ? fields : [
            {
                label: 'Search',
                value: 'All contractors'
            }
        ];
    },
    isValidOutput: (output)=>{
        return typeof output === 'object' && output !== null && 'success' in output && 'contractors' in output;
    },
    isEmptyResult: (output)=>!output.contractors?.length,
    isErrorResult: (output)=>!output.success
});
// ============================================================================
// Export
// ============================================================================
export const gsaToolConfigs = [
    gsaPricingConfig,
    gsaContractorLookupConfig
];
